const POST_AUTH_REDIRECT_KEY = 'post_auth_redirect';
const POST_AUTH_REDIRECT_TTL_MS = 10 * 60 * 1000;

// The auth UI pushes /login and /signup over the current URL while the modal
// is open, so "current URL" at submit time can be an auth path. Those must
// never be saved or used as a post-auth destination, or the login card
// reopens after a successful login.
const AUTH_PATHS = ['/login', '/signup'];

const isAuthPath = (path: string): boolean => {
  try {
    return AUTH_PATHS.includes(new URL(path, window.location.origin).pathname);
  } catch {
    return false;
  }
};

interface StoredPostAuthRedirect {
  path: string;
  createdAt: number;
}

const currentSameOriginPath = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const sameOriginPath = (rawUrl: string): string | null => {
  try {
    const url = new URL(rawUrl, window.location.origin);

    if (url.origin !== window.location.origin) return null;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const encodeRedirect = (path: string): string => JSON.stringify({
  path,
  createdAt: Date.now(),
} satisfies StoredPostAuthRedirect);

const getStorageRedirect = (storage: Storage): string | null => {
  const raw = storage.getItem(POST_AUTH_REDIRECT_KEY);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPostAuthRedirect>;

    if (
      typeof parsed.path !== 'string'
      || typeof parsed.createdAt !== 'number'
      || Date.now() - parsed.createdAt > POST_AUTH_REDIRECT_TTL_MS
    ) {
      storage.removeItem(POST_AUTH_REDIRECT_KEY);

      return null;
    }

    return sameOriginPath(parsed.path);
  } catch {
    return sameOriginPath(raw);
  }
};

const readRedirect = (): string | null => (
  getStorageRedirect(sessionStorage) || getStorageRedirect(localStorage)
);

const clearRedirect = (): void => {
  sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  localStorage.removeItem(POST_AUTH_REDIRECT_KEY);
};

export const getPostAuthRedirectPath = (url: string = window.location.href): string | null => sameOriginPath(url);

export const savePostAuthRedirect = (url: string = window.location.href): void => {
  const safePath = sameOriginPath(url);

  if (!safePath || isAuthPath(safePath)) return;

  const encoded = encodeRedirect(safePath);

  sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, encoded);
  localStorage.setItem(POST_AUTH_REDIRECT_KEY, encoded);
};

export const consumePostAuthRedirect = (): string | null => {
  const path = readRedirect();

  clearRedirect();

  return path;
};

const sanitizeTarget = (path: string | null): string | null => (
  path && !isAuthPath(path) ? path : null
);

export const redirectAfterAuth = (fallbackUrl: string = window.location.href): void => {
  const fallbackPath = sanitizeTarget(sameOriginPath(fallbackUrl));
  const target = sanitizeTarget(consumePostAuthRedirect()) || fallbackPath || '/';

  if (target === currentSameOriginPath()) {
    window.location.reload();

    return;
  }

  window.location.href = target;
};
